import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import {
  useClearEquipmentCache,
  useExportEquipment,
  useListEquipment,
} from "#hooks/adapters/useEquipment";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { linkToEquipmentDetail, linkToFacilities } from "#routers/links";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus";
import { TemplatedConfigurationRunStatusWithQueue } from "#src/batteries-included-components/Banners/TemplatedConfigurationRunStatus";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import EditEquipmentDialog from "#src/batteries-included-components/Dialogs/EditEquipmentDialog";
import {
  EquipmentTableFilterArea,
  EquipmentViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink/RoutingLink";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  ItemAction,
  Pill,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  EquipmentAdapter,
  EquipmentAdapterQueryKeys,
  EquipmentStatus,
  EquipmentType,
  ResourceDefinitions,
  Resources,
  SortDirection,
  type GroupedByEntryType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  toFlattenedObject,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

const importDataActionQueueKey = getImportDataActionQueueKey({
  resource: ResourceDefinitions.equipment,
});

export const EquipmentList = () => {
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [assetsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "assets-shared"
  );
  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    ["equipment", "list"]
  );

  const { toast } = useToast();
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [equipmentToDelete, setEquipmentToDelete] = useState<EquipmentType>();
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const [viewFilters] = useStickyState({}, assetsSharedViewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const rawFilters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );

  // @ts-expect-error TODO: Typing on filter
  const { period, ...filters } = rawFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "facility_name",
      sortDirection: SortDirection.ASCENDING,
    },
    {
      ...filters,
      period,
    },
    tableStateStorageKey
  );

  const {
    customAttributeHeaders: equipmentCustomAttributeHeaders,
    isLoading: isCustomAttributesLoading,
  } = useCustomAttributeFilters({
    assetType: AssetType.EQUIPMENT,
    section: `${localize("Equipment")}`,
    groupable: true,
  });

  const params: Parameters<typeof EquipmentAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: toFlattenedObject(filters),
    meta: {
      // @ts-expect-error TODO: Typing on filter
      period: getYearMonthFromDateRange(period),
    },
  };
  const { data, isLoading, isFetching, refetch } = useListEquipment(params);
  const items = data?.data ?? [];

  const { invalidate } = useClearEquipmentCache();

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...params,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? params)
  );

  const equipmentGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: EquipmentAdapterQueryKeys.getList(d.payload),
      queryFn: () => EquipmentAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? equipmentGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const { mutate: handleExport, isLoading: isExporting } = useExportEquipment({
    filters: toFlattenedObject(filters),
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
    meta: {
      // @ts-expect-error TODO: Typing on filter
      period: getYearMonthFromDateRange(period),
    },
  });

  const deleteEquipmentOnClick = async () => {
    try {
      if (equipmentToDelete) {
        await EquipmentAdapter.deleteOne({
          id: equipmentToDelete.id,
        });

        toast.push({
          intent: "success",
          description: `Successfully deleted '${equipmentToDelete.name}'`,
        });
      }
    } finally {
      invalidate();
      setEquipmentToDelete(undefined);
    }
  };

  const rowActionItems = ({ item }: { item: EquipmentType }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setAssetIdToEdit(item.id),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => setEquipmentToDelete(item),
        icon: "trash",
      },
    },
  ];

  const fixedHeaders: Array<HeaderType<EquipmentType>> = useMemo(
    () => [
      {
        label: `${localize("Equipment")} Name`,
        key: "name",
        isSortable: true,
        renderComponent: ({ item }: { item: EquipmentType }) => (
          <RoutingLink to={linkToEquipmentDetail(item?.id)}>
            {item.name}
          </RoutingLink>
        ),
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        variant: "pill",
        renderComponent: ({ item }: { item: EquipmentType }) => (
          <Pill
            variant={
              item.status === EquipmentStatus.ACTIVE ? "success" : "default"
            }
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: `${localize("Equipment")} Type`,
        key: "type",
        isSortable: true,
        isGroupable: true,
        groupByKeys: ["type.name"],
        renderComponent: ({ item }: { item: EquipmentType }) =>
          item?.type?.name ?? "-",
      },
      {
        label: `${localize("Facility")} Name`,
        key: "facility.name",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }: { item: EquipmentType }) => (
          <RoutingLink to={linkToFacilities(item?.facility_id)}>
            {item?.facility?.name ?? ""}
          </RoutingLink>
        ),
      },
      {
        label: "Effective Date",
        key: "effective_date",
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell value={item.effective_date} />
        ),
        isSortable: true,
      },
    ],
    [isMappingLoading]
  );
  const headers = useMemo(
    () => [
      ...fixedHeaders,
      ...equipmentCustomAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [
      fixedHeaders,
      equipmentCustomAttributeHeaders,
      metaHeaders,
      metaExtendedHeaders,
    ]
  );

  const actionRow = (
    <>
      <PermissionAwareExportButton
        key="export-equipment"
        onClick={handleExport}
        isExporting={isExporting}
      />
      {isDataIngestionEnabled ? (
        <ImportDataAction
          key="import-equipment"
          modes={["single-entity", "templated-configuration"]}
          resource={{
            ...ResourceDefinitions.equipment,
            label: {
              singular: localize(ResourceDefinitions.equipment.label.singular),
              plural: localize("equipment_plural"),
            },
          }}
        />
      ) : null}
    </>
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: equipmentGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
    getItemActions: rowActionItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Equipment list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  return (
    <>
      {isDataIngestionEnabled ? (
        <ImportDataActionStatusWithQueue queueKey={importDataActionQueueKey} />
      ) : null}
      <TemplatedConfigurationRunStatusWithQueue
        primaryResourceType={Resources.EQUIPMENT}
      />
      <EquipmentViewFilterArea
        viewConfigStorageKey={assetsSharedViewConfigStorageKey}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          actionRow,
        }}
        filterComponent={
          <EquipmentTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          intent: "simplicity-first",
          isLoading: isLoading || isCustomAttributesLoading,
          isBusy: isFetching,
          items,
          getItemId: (item) => item.id,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          headers,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
          isFluid: false,
          getItemActions: rowActionItems,
        }}
        {...groupByDataTableProps}
      />
      <Dialog
        isOpen={!!equipmentToDelete}
        onClose={() => setEquipmentToDelete(undefined)}
        title={`Delete "${equipmentToDelete?.name}"`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={deleteEquipmentOnClick}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete this {localize("equipment")}? This
          action cannot be undone.
        </div>
      </Dialog>
      <EditEquipmentDialog
        defaultPeriod={getYearMonthFromDateRange(period) ?? ""}
        onClose={() => setAssetIdToEdit(undefined)}
        equipmentId={assetIdToEdit}
        onEdit={() => {
          refetch();
        }}
      />
    </>
  );
};
